import { EditorView as CMEditorView, basicSetup } from 'codemirror';
import { html } from '@codemirror/lang-html';
import { oneDark } from '@codemirror/theme-one-dark';
import { indentSelection } from '@codemirror/commands'
import { parseOptions } from '../components/utils';

type Language = 'html' | string;

interface CodeMirrorOptions {
  language?: Language;
  [key: string]: unknown;
}

const codeMirrors = Array.from(document.querySelectorAll('[data-codemirror]')) as HTMLTextAreaElement[];
const defaultOptions: CodeMirrorOptions = {
  language: 'html'
};

codeMirrors.forEach((codeMirror: HTMLTextAreaElement) => {
  const raw = codeMirror.dataset.codemirror ?? null;
  const fullOptions = parseOptions<CodeMirrorOptions>(raw, defaultOptions);

  let view: any;
  switch (fullOptions.language) {
    case 'html':
      view = new (CMEditorView as any)({
        doc: codeMirror.value,
        extensions: [basicSetup, html(), oneDark],
      });
      break;
    default:
      view = new (CMEditorView as any)({
        doc: codeMirror.value,
        extensions: [basicSetup, oneDark],
      });
      break;
  }

  // AUTOINDENT ON LOAD � run after the editor DOM is attached
  runWhenAttached(view, () => {
    view.dispatch({
      selection: {
        anchor: 0,
        head: view.state.doc.length
      }
    });

    (indentSelection as any)({
      state: view.state,
      dispatch: (transaction: any) => {
        view.dispatch(transaction);
        view.dispatch({
          selection: {
            anchor: 0,
            head: 0
          }
        });
      }
    });
  });

  // insert editor before the original textarea and hide the textarea
  codeMirror.parentNode?.insertBefore(view.dom, codeMirror);
  (codeMirror.style as CSSStyleDeclaration).display = 'none';

  if (codeMirror.form) {
    codeMirror.form.addEventListener('submit', () => {
      codeMirror.value = view.state.doc.toString();
    });
  }
});

function runWhenAttached(view: any, fn: () => void) {
  // If already in the document, run immediately
  if (document.contains(view.dom)) {
    fn();
    return;
  }

  // Observe DOM until the view.dom is attached
  const obs = new MutationObserver(() => {
    if (document.contains(view.dom)) {
      obs.disconnect();
      fn();
    }
  });

  obs.observe(document, { childList: true, subtree: true });

  // Safety fallback: if not attached within 2s, run anyway
  const fallback = setTimeout(() => {
    try { obs.disconnect(); } catch { /* ignore */ }
    fn();
  }, 2000);

  // Ensure we clear fallback when we actually run
  const wrapped = () => { clearTimeout(fallback); fn(); };
  // replace fn in obs callback by wrapped:
  obs.disconnect();
  obs.observe(document, { childList: true, subtree: true });
  const newObs = new MutationObserver(() => {
    if (document.contains(view.dom)) {
      newObs.disconnect();
      wrapped();
    }
  });
  newObs.observe(document, { childList: true, subtree: true });
  // If already attached between checks
  if (document.contains(view.dom)) {
    newObs.disconnect();
    wrapped();
  }
}
